import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, mainSiteUrl } from '@/mocks/siteData';
import { articlesContent } from '@/mocks/articlesContent';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.study';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const content = slug ? articlesContent[slug] : undefined;

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-20">
          <div className="w-full px-4 md:px-6 max-w-lg mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-4xl font-bold text-foreground-900 mb-4">404</h1>
            <p className="text-base text-foreground-600 mb-8">
              This page could not be found. The article or resource you are looking for may have moved or does not exist.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleUrl = `${SITE_URL}/${article.slug}`;

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    url: articleUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Study',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Study',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    about: {
      '@type': 'Thing',
      name: 'Basetao Spreadsheet',
    },
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const relatedArticles = articles
    .filter((a) => a.slug !== slug)
    .slice(0, 2);

  const renderSection = (section: (typeof content.sections)[0], idx: number) => {
    switch (section.type) {
      case 'h2':
        return <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">{section.content}</h2>;
      case 'h3':
        return <h3 key={idx} className="font-heading text-lg font-semibold text-foreground-800 mt-6 mb-2">{section.content}</h3>;
      case 'p':
        return <p key={idx} className="text-sm md:text-base leading-relaxed text-foreground-700">{section.content}</p>;
      case 'ul':
        return (
          <ul key={idx} className="list-disc pl-5 space-y-2 text-sm md:text-base text-foreground-700">
            {section.items?.map((item, i) => (
              <li key={i} className="leading-relaxed"><strong>{item.split(':')[0]}:</strong>{item.split(':').slice(1).join(':')}</li>
            ))}
          </ul>
        );
      case 'table':
        return (
          <div key={idx} className="overflow-x-auto rounded-lg border border-background-200/70 my-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-200">
                  {section.headers?.map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {section.rows?.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                    {section.headers?.map((h) => (
                      <td key={h} className="px-4 py-3 text-foreground-600 whitespace-nowrap">{row[h]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
      />
      <Navbar />

      <main>
        <article>
          <header className="pt-24 pb-12 md:pt-32 md:pb-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <Link to="/" className="hover:text-primary-600 transition-colors">{article.category}</Link>
                <span>/</span>
                <span className="text-foreground-800 font-medium">{article.title}</span>
              </nav>
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                {article.category} &middot; 2026
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: {article.date}</span>
                <span>&middot;</span>
                <span>{article.readTime}</span>
                <span>&middot;</span>
                <span>Educational Resource</span>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto py-10 md:py-14">
            <div className="mb-10 rounded-lg overflow-hidden border border-background-200/70">
              <img src={article.image} alt={article.title} className="w-full aspect-video object-cover" />
            </div>

            <div className="space-y-4">
              {content.sections.map((section, idx) => renderSection(section, idx))}
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70 mt-10">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                Ready to Start Shopping Smarter?
              </h3>
              <p className="text-sm text-foreground-600 mb-4">
                Now that you have learned about {article.title.toLowerCase()}, take the next step. Visit the main website to explore product categories and start building your own organized shopping workflow today.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-shopping-bag-3-line"></i>
                  Basetao Spreadsheet
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-primary-600 border border-primary-200 hover:border-primary-300 hover:bg-primary-50 rounded-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </article>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">Related Articles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedArticles.map((item) => (
                <Link
                  key={item.slug}
                  to={`/${item.slug}`}
                  className="group flex gap-3 p-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all"
                >
                  <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex flex-col justify-center">
                    <span className="text-xs font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">{item.title}</span>
                    <span className="text-xs text-foreground-400 mt-1">{item.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}